import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

export function Register() {
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      return;
    }

    if (password.length < 8) {
      setError('Mật khẩu phải có ít nhất 8 ký tự');
      return;
    }

    setLoading(true);
    try {
      await register(email, password, displayName || undefined);
      setSuccess(true);
      // Auto login after registration
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đăng ký thất bại');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg py-12 px-4">
        <div className="w-full max-w-md">
          <div className="card p-8 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text mb-2 display">Đăng ký thành công!</h1>
            <p className="text-text-muted mb-6">Đang chuyển hướng đến bảng điều khiển...</p>
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand border-t-transparent mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg py-12 px-4">
      <div className="w-full max-w-md">
        <div className="card p-8">
          <div className="text-center mb-8">
            <Link to="/" className="text-xl font-bold text-brand display">OCR Invoice</Link>
            <h1 className="text-2xl font-bold text-text mt-6 display">Tạo tài khoản</h1>
            <p className="text-text-muted mt-2">Bắt đầu dùng thử miễn phí 7 ngày gói Pro</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 text-sm text-error bg-error/10 rounded-lg" role="alert">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="display-name" className="label">
                Tên hiển thị (tùy chọn)
              </label>
              <input
                id="display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="input w-full mt-1"
                placeholder="Nguyễn Văn A"
                autoComplete="name"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input w-full mt-1"
                placeholder="you@example.com"
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Mật khẩu
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input w-full mt-1"
                placeholder="Ít nhất 8 ký tự"
                required
                autoComplete="new-password"
                disabled={loading}
                minLength={8}
              />
              <p className="text-xs text-text-muted mt-1">Tối thiểu 8 ký tự</p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="label">
                Xác nhận mật khẩu
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input w-full mt-1"
                placeholder="Nhập lại mật khẩu"
                required
                autoComplete="new-password"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 focus-ring"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Đang tạo tài khoản...
                </span>
              ) : (
                'Tạo tài khoản'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-text-muted text-sm">
              Đã có tài khoản?{' '}
              <Link to="/login" className="text-brand hover:underline font-medium focus-ring">
                Đăng nhập ngay
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center text-xs text-text-muted">
            <p>Bằng cách tạo tài khoản, bạn đồng ý với</p>
            <div className="flex justify-center gap-4 mt-1">
              <a href="#" className="text-brand hover:underline focus-ring">Điều khoản dịch vụ</a>
              <a href="#" className="text-brand hover:underline focus-ring">Chính sách quyền riêng tư</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}